import {DateRange} from '@blueprintjs/datetime2';
import {ChartData} from 'chart.js';
import * as I from 'immutable';
import React from 'react';

import {PieGroup} from 'app/components/Dashboard/GenericChart/PieGroupControls';
import {BaseLensStatsWidget} from 'app/components/Dashboard/OrgLensStats/BaseLensStatsWidget';
import {
  getLensStatsPercentLoaded,
  makePieChartCountData,
} from 'app/components/Dashboard/utils/chartUtils';
import {ApiOrganization} from 'app/modules/Remote/Organization';

import {useOrgReportStats} from '../hooks/useOrgReportStats';
import {FeatureMetadataApiResponse, ReportsQueryParams} from '../types';
import {DrilldownTableRow} from './LensStatsDrilldownTable';

/**
 * Widget that displays a pie chart of report counts by portfolio or author
 */
const VALID_REPORT_GROUPINGS: PieGroup[] = [
  {
    label: 'Portfolio',
    id: 'portfolio',
    singleMembership: true,
    groupingField: 'projectId',
    labelField: 'projectName',
  },
  {
    label: 'Author',
    id: 'author',
    singleMembership: true,
    groupingField: 'author',
    labelField: 'author',
  },
];

export const OrgReportsWidget: React.FC<{
  organization: I.ImmutableOf<ApiOrganization>;
  featureMetadata: FeatureMetadataApiResponse[] | undefined;
}> = ({organization, featureMetadata}) => {
  const orgId = organization.get('id');
  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const [selectedPieGroup, setSelectedPieGroup] = React.useState<PieGroup>(
    VALID_REPORT_GROUPINGS[0]
  );

  const [reportParams, setReportParams] = React.useState<ReportsQueryParams>({
    startDate: lastYear.toISOString(),
    endDate: today.toISOString(),
  });

  const {
    data: reportsData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useOrgReportStats(orgId, reportParams);

  const chartData: ChartData<'pie'> = React.useMemo(() => {
    if (!reportsData) {
      return {labels: [], datasets: []} as ChartData<'pie'>;
    }

    return makePieChartCountData(reportsData, selectedPieGroup);
  }, [reportsData, selectedPieGroup]);

  const percentLoaded = getLensStatsPercentLoaded(reportsData);

  // Continuously fetch data page by page so we can watch our data stream in
  // This is important for orgs with hundreds of features
  React.useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  const tableData = React.useMemo<DrilldownTableRow[]>(() => {
    if (!featureMetadata) return [];

    const rows: DrilldownTableRow[] = [];

    featureMetadata.forEach((featureDict) => {
      Object.entries(featureDict).forEach(([featureId, feature]) => {
        rows.push({
          featureId: featureId,
          projectId: feature.projectId,
          projectName: feature.projectName || 'Unknown Portfolio',
          featureName: feature.name || 'Unknown Property',
          propertyTags: feature.tags || [],
          organizationId: orgId,
        });
      });
    });

    const sortedRows = rows.sort((a, b) => a.projectName.localeCompare(b.projectName));
    return sortedRows;
  }, [featureMetadata, orgId]);

  return (
    <BaseLensStatsWidget
      error={error}
      widgetConfig={{
        title: `Report Counts by ${selectedPieGroup.label}`,
        type: 'chart',
        id: -1,
        isPublished: true,
      }}
      loading={!!(isFetching || isFetchingNextPage)}
      showControls={true}
      showDateRangeInput={true}
      dateRange={[
        reportParams.startDate ? new Date(reportParams.startDate) : null,
        reportParams.endDate ? new Date(reportParams.endDate) : null,
      ]}
      setDateRange={(dateRange: DateRange) =>
        setReportParams({
          ...reportParams,
          startDate: dateRange[0]?.toISOString() ?? undefined,
          endDate: dateRange[1]?.toISOString() ?? undefined,
        })
      }
      pieGroups={VALID_REPORT_GROUPINGS}
      selectedPieGroup={selectedPieGroup}
      setSelectedPieGroup={setSelectedPieGroup}
      chartData={chartData}
      plugins={[]}
      countItemLabel="reports"
      percentLoaded={percentLoaded}
      organization={organization}
      tableData={tableData}
    />
  );
};
